import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminSidebar from "./AdminSidebar";
import axios from "../../api/adminAxios";

interface restaurantData {
    id: number;
    name: string;
}

interface subscriptionsData {
    id: number;
    name: string;
    price_monthly: number;
    price_yearly: number;
}

const AdminDashboard = () => {
    const [restaurants, setRestaurants] = useState<restaurantData[]>([]);
    const [subscriptions, setSubscriptions] = useState<subscriptionsData[]>([]);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;


    useEffect(() => {
        const fetchAdminData = async () => {
            try {
                await axios.get("/admin/dashboard");

                const restaurantsRes = await axios.get<restaurantData[]>("/admin/restaurants");
                setRestaurants(restaurantsRes.data);

                const subscriptionsRes = await axios.get<subscriptionsData[]>("/admin/subscriptions");
                setSubscriptions(subscriptionsRes.data);
            } catch (error: any) {
                console.error("Admin Dashboard Error:", error);
                if (error.response?.status === 401) {
                    localStorage.removeItem("admin_access_token");
                    navigate("/admin/login");
                }
            }
        };
        fetchAdminData();
    }, []);
    const totalPages = Math.ceil(restaurants.length / itemsPerPage);
    const paginatedRestaurants = restaurants.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );


    const calculateDiscount = (monthly: number, yearly: number) => {
        const totalMonthlyCost = monthly * 12;
        const discount = ((totalMonthlyCost - yearly) / totalMonthlyCost) * 100;
        return Math.round(discount);
    };

    return (
        <div className="min-h-screen flex">
            <AdminSidebar />

            <div className="flex-1 p-6 bg-gray-100">
                <h3 className="text-2xl font-bold mb-4">📊 Admin Dashboard</h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Restaurants */}
                    <div className="bg-white shadow-md p-4 rounded-lg">
                        <h4 className="text-lg font-semibold mb-2">🍽️ Restaurants</h4>
                        <ul className="text-gray-700 text-sm">
                            {paginatedRestaurants.map((r) => (
                                <li key={r.id} className="border-b py-2">{r.name}</li>
                            ))}
                        </ul>

                        {/* Pagination Controls */}
                        <div className="flex justify-between items-center mt-4 text-sm text-gray-600">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                                className={`px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-200' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                            >
                                ◀ Prev
                            </button>
                            <span>Page {currentPage} of {totalPages}</span>
                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                                disabled={currentPage === totalPages}
                                className={`px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-200' : 'bg-blue-500 text-white hover:bg-blue-600'}`}
                            >
                                Next ▶
                            </button>
                        </div>

                    </div>
                    <div className="bg-white shadow-md p-4 rounded-lg">
                        <h4 className="text-lg font-semibold mb-2">💳 Subscriptions</h4>
                        <ul className="text-gray-700 text-sm">
                            {subscriptions.map((s) => {
                                const discount = calculateDiscount(s.price_monthly, s.price_yearly);
                                return (
                                    <li key={s.id} className="border-b py-3">
                                        <span className="font-bold">{s.name}</span><br />
                                        <span>${s.price_monthly}/mo</span>{" "}
                                        <span className="text-gray-500">|</span>{" "}
                                        <span>${s.price_yearly}/yr</span>{" "}
                                        <span className="ml-2 text-green-600 text-sm font-semibold">
                                            {discount}% off
                                        </span>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
